/**
 * Copper Magazine - Interactive Features
 * Uses Cloudflare Worker for all issue data (dropdown + article content)
 */

class CopperMagazine {
  constructor() {
    this.searchOverlay = document.getElementById('search-overlay');
    this.searchInput = document.getElementById('search-input');
    this.searchResults = document.getElementById('search-results');
    this.allResultItems = [];
    
    // Article load more
    this.articlesGrid = document.querySelector('.copper-grid__articles');
    this.hiddenArticles = document.getElementById('hidden-articles');
    this.loadMoreArticlesBtn = document.getElementById('load-more-articles');
    
    // Issues load more
    this.archiveGrid = document.getElementById('archive-grid');
    this.loadMoreIssuesBtn = document.getElementById('load-more-issues');
    this.archiveNote = document.getElementById('archive-note');
    
    // Current issue section
    this.currentIssueSection = document.getElementById('current-issue');
    this.masthead = document.querySelector('.copper-masthead');
    this.editorial = document.querySelector('.copper-editorial');
    this.articleGrid = document.querySelector('.copper-grid');
    
    // Storefront API config (fallback only)
    this.storefrontToken = '3312145be329e2761e3e83be84385a4a';
    this.shopDomain = window.location.hostname;
    
    // Cloudflare Worker URL (primary source)
    this.workerUrl = 'https://copper-issues.psaudio.workers.dev';
    this.manifestUrl = this.workerUrl + '/manifest';
    
    // Track loaded issues
    this.loadedIssues = new Set();
    this.lowestIssueLoaded = 999;
    this.isLoadingIssues = false;
    this.blogHandle = 'copper';
    this.currentDisplayedIssue = null;
    this.latestIssueNum = null;
    
    this.init();
  }

  init() {
    this.bindEvents();
    
    if (this.searchResults) {
      this.allResultItems = Array.from(this.searchResults.querySelectorAll('.copper-search-result-item'));
    }
    
    document.querySelectorAll('.copper-issue-card[data-issue]').forEach(card => {
      const issueNum = parseInt(card.dataset.issue);
      if (issueNum) {
        this.loadedIssues.add(issueNum);
        if (issueNum < this.lowestIssueLoaded) {
          this.lowestIssueLoaded = issueNum;
        }
      }
    });
    
    if (this.loadMoreIssuesBtn) {
      this.blogHandle = this.loadMoreIssuesBtn.dataset.blogHandle || 'copper';
      const lowestFromData = parseInt(this.loadMoreIssuesBtn.dataset.lowestIssue);
      if (lowestFromData && lowestFromData < this.lowestIssueLoaded) {
        this.lowestIssueLoaded = lowestFromData;
      }
    }
    
    const mastheadNum = document.querySelector('.copper-masthead__number');
    if (mastheadNum) {
      const match = mastheadNum.textContent.match(/Issue\s+(\d+)/i);
      if (match) {
        this.currentDisplayedIssue = parseInt(match[1]);
        const urlParams = new URLSearchParams(window.location.search);
        if (!urlParams.has('issue')) {
          this.latestIssueNum = this.currentDisplayedIssue;
        }
      }
    }
    
    const currentIssueSection = document.getElementById('current-issue');
    if (currentIssueSection && currentIssueSection.dataset.latestIssue) {
      this.latestIssueNum = parseInt(currentIssueSection.dataset.latestIssue);
    }
    
    if (!this.latestIssueNum && this.currentDisplayedIssue) {
      const urlParams = new URLSearchParams(window.location.search);
      if (!urlParams.has('issue')) {
        this.latestIssueNum = this.currentDisplayedIssue;
      }
    }
    
    console.log('Copper Magazine initialized');
    console.log('Current displayed issue:', this.currentDisplayedIssue);
    
    this.loadAllTOCsForDropdown();
    this.checkURLForIssue();
  }
  
  async loadAllTOCsForDropdown() {
    const dropdownList = document.getElementById('issues-dropdown-list');
    if (!dropdownList) return;
    
    // Load from Worker manifest
    try {
      console.log('Loading issues from manifest...');
      const response = await fetch(this.manifestUrl);
      
      if (response.ok) {
        const manifest = await response.json();
        
        if (manifest.issues && manifest.issues.length > 0) {
          console.log(`Manifest loaded: ${manifest.totalIssues} issues`);
          
          dropdownList.innerHTML = manifest.issues.map(issue => 
            `<a href="${issue.url}" class="copper-nav-dropdown__item" data-issue="${issue.num}">Issue ${issue.num}</a>`
          ).join('');
          
          this.highlightCurrentIssueInDropdown();
          return;
        }
      }
    } catch (e) {
      console.log('Manifest not available:', e.message);
    }
    
    // Fallback to Storefront API
    try {
      const query = `
        query GetAllTOCs {
          articles(first: 250, query: "title:*Table of Contents*", sortKey: PUBLISHED_AT, reverse: true) {
            edges {
              node {
                title
                handle
                tags
              }
            }
          }
        }
      `;
      
      const response = await fetch(`https://${this.shopDomain}/api/2024-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken
        },
        body: JSON.stringify({ query })
      });
      
      const data = await response.json();
      
      if (data.errors || !data.data?.articles?.edges) {
        dropdownList.innerHTML = '<div class="copper-nav-dropdown__error">Could not load issues</div>';
        return;
      }
      
      const issues = [];
      data.data.articles.edges.forEach(({ node }) => {
        if (node.title.includes('Table of Contents')) {
          const issueTag = node.tags.find(t => t.includes('Category_Issue'));
          if (issueTag) {
            const issueNum = parseInt(issueTag.replace('Category_Issue ', '').trim());
            if (issueNum > 0) {
              issues.push({
                num: issueNum,
                url: `/blogs/${this.blogHandle}?issue=${issueNum}`
              });
            }
          }
        }
      });
      
      issues.sort((a, b) => b.num - a.num);
      
      if (issues.length > 0) {
        dropdownList.innerHTML = issues.map(issue => 
          `<a href="${issue.url}" class="copper-nav-dropdown__item" data-issue="${issue.num}">Issue ${issue.num}</a>`
        ).join('');
        
        this.highlightCurrentIssueInDropdown();
      } else {
        dropdownList.innerHTML = '<div class="copper-nav-dropdown__error">No issues found</div>';
      }
      
    } catch (error) {
      console.error('Error loading TOCs:', error);
      dropdownList.innerHTML = '<div class="copper-nav-dropdown__error">Could not load issues</div>';
    }
  }

  bindEvents() {
    document.querySelectorAll('[data-action]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleAction(e);
      });
    });

    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => {
        this.filterArticles(this.searchInput.value);
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.searchOverlay?.classList.contains('active')) {
        this.closeSearch();
      }
    });

    this.searchOverlay?.addEventListener('click', (e) => {
      if (e.target === this.searchOverlay) {
        this.closeSearch();
      }
    });
    
    if (this.loadMoreArticlesBtn) {
      this.loadMoreArticlesBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.loadMoreArticles();
      });
    }
    
    if (this.loadMoreIssuesBtn) {
      this.loadMoreIssuesBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.loadMoreIssuesViaAPI();
      });
    }
    
    document.addEventListener('click', (e) => {
      const dropdown = document.querySelector('.copper-nav-dropdown');
      if (dropdown && dropdown.classList.contains('is-open')) {
        if (!dropdown.contains(e.target)) {
          this.closeIssuesDropdown();
        }
      }
      
      const dropdownItem = e.target.closest('.copper-nav-dropdown__item');
      if (dropdownItem) {
        this.closeIssuesDropdown();
        return;
      }
      
      const issueCard = e.target.closest('.copper-issue-card[data-issue]');
      if (issueCard) {
        if (this.currentIssueSection) {
          e.preventDefault();
          const issueNum = parseInt(issueCard.dataset.issue);
          if (issueNum) {
            this.loadIssue(issueNum);
          }
        }
      }
    });
  }

  handleAction(e) {
    const action = e.currentTarget.dataset.action;
    
    switch(action) {
      case 'scroll-to-current':
        if (this.latestIssueNum && this.currentDisplayedIssue !== this.latestIssueNum) {
          this.loadIssue(this.latestIssueNum);
        } else {
          document.getElementById('current-issue')?.scrollIntoView({ behavior: 'smooth' });
        }
        break;
      case 'scroll-to-archive':
        document.getElementById('archive')?.scrollIntoView({ behavior: 'smooth' });
        break;
      case 'toggle-issues-dropdown':
        this.toggleIssuesDropdown();
        break;
      case 'open-search':
        this.openSearch();
        break;
      case 'close-search':
        this.closeSearch();
        break;
      case 'download-pdf':
        this.downloadPDF(e.currentTarget.dataset.issue);
        break;
    }
  }
  
  toggleIssuesDropdown() {
    const dropdown = document.querySelector('.copper-nav-dropdown');
    if (dropdown) {
      dropdown.classList.toggle('is-open');
      if (dropdown.classList.contains('is-open')) {
        this.highlightCurrentIssueInDropdown();
      }
    }
  }
  
  closeIssuesDropdown() {
    const dropdown = document.querySelector('.copper-nav-dropdown');
    if (dropdown) {
      dropdown.classList.remove('is-open');
    }
  }
  
  highlightCurrentIssueInDropdown() {
    const dropdown = document.querySelector('.copper-nav-dropdown');
    if (!dropdown) return;
    
    dropdown.querySelectorAll('.copper-nav-dropdown__item').forEach(item => {
      item.classList.remove('is-current');
    });
    
    const currentItem = dropdown.querySelector(`[data-issue="${this.currentDisplayedIssue}"]`);
    if (currentItem) {
      currentItem.classList.add('is-current');
      currentItem.scrollIntoView({ block: 'center' });
    }
  }
  
  checkURLForIssue() {
    const urlParams = new URLSearchParams(window.location.search);
    const issueParam = urlParams.get('issue');
    
    if (issueParam) {
      const issueNum = parseInt(issueParam);
      if (issueNum && issueNum !== this.currentDisplayedIssue) {
        console.log('Loading issue from URL:', issueNum);
        this.loadIssue(issueNum);
      }
    }
  }
  
  async loadIssue(issueNum) {
    console.log('Loading issue:', issueNum);
    
    if (this.currentIssueSection) {
      this.currentIssueSection.style.opacity = '0.5';
    }
    
    try {
      // Try Worker first (works for ALL issues)
      let issueData = await this.fetchIssueFromWorker(issueNum);
      
      // Fallback to Storefront API if Worker fails
      if (!issueData || !issueData.toc) {
        console.log('Worker failed, trying Storefront API...');
        issueData = await this.fetchIssueData(issueNum);
      }
      
      if (issueData && issueData.toc) {
        const newURL = new URL(window.location);
        if (issueNum === this.latestIssueNum) {
          newURL.searchParams.delete('issue');
        } else {
          newURL.searchParams.set('issue', issueNum);
        }
        window.history.pushState({issue: issueNum}, '', newURL);
        
        this.renderIssue(issueData);
        this.currentDisplayedIssue = issueNum;
        
        this.currentIssueSection?.scrollIntoView({ behavior: 'smooth' });
      } else {
        console.warn('No data returned for issue', issueNum);
        this.showOldIssueMessage(issueNum);
      }
    } catch (error) {
      console.error('Error loading issue:', error);
      this.showOldIssueMessage(issueNum);
    }
    
    if (this.currentIssueSection) {
      this.currentIssueSection.style.opacity = '1';
    }
  }
  
  // Fetch issue from Cloudflare Worker (Admin API - has ALL issues)
  async fetchIssueFromWorker(issueNum) {
    try {
      console.log('Fetching issue from Worker:', issueNum);
      const response = await fetch(`${this.workerUrl}/issue/${issueNum}`);
      
      if (!response.ok) {
        console.log('Worker returned error:', response.status);
        return null;
      }
      
      const data = await response.json();
      console.log('Worker response for issue', issueNum, ':', data);
      
      if (data.error || !data.toc) {
        return null;
      }
      
      // Transform Worker response to match expected format
      return {
        number: data.issueNum,
        date: data.date,
        toc: data.toc,
        articles: data.articles || []
      };
      
    } catch (e) {
      console.error('Worker fetch error:', e);
      return null;
    }
  }
  
  showOldIssueMessage(issueNum) {
    console.log('Navigating directly to TOC for issue', issueNum);
    window.location.href = `/blogs/${this.blogHandle}/issue-${issueNum}`;
  }
  
  // Fallback: Fetch from Storefront API (only has recent issues)
  async fetchIssueData(issueNum) {
    const query = `
      query GetIssueArticles($query: String!, $first: Int!) {
        articles(first: $first, query: $query, sortKey: PUBLISHED_AT, reverse: true) {
          edges {
            node {
              id
              title
              handle
              publishedAt
              tags
              excerpt
              contentHtml
              image {
                url
              }
              blog {
                handle
              }
            }
          }
        }
      }
    `;
    
    const response = await fetch(`https://${this.shopDomain}/api/2024-01/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.storefrontToken
      },
      body: JSON.stringify({
        query,
        variables: {
          query: `tag:"Category_Issue ${issueNum}"`,
          first: 100
        }
      })
    });
    
    const data = await response.json();
    
    if (data.errors || !data.data?.articles?.edges?.length) {
      return null;
    }
    
    const articles = data.data.articles.edges.map(({ node }) => {
      let author = '';
      let category = '';
      
      node.tags.forEach(tag => {
        if (tag.startsWith('Author_')) {
          author = tag.replace('Author_', '');
        }
        if (tag.startsWith('Subcategory_')) {
          category = tag.replace('Subcategory_', '');
        }
      });
      
      return {
        ...node,
        image: node.image?.url || null,
        author,
        category,
        isTOC: node.title.includes('Table of Contents')
      };
    });
    
    const tocArticle = articles.find(a => a.isTOC);
    const contentArticles = articles.filter(a => !a.isTOC);
    
    if (!tocArticle) {
      return null;
    }
    
    const date = new Date(tocArticle.publishedAt);
    const monthYear = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    
    return {
      number: issueNum,
      date: monthYear,
      toc: tocArticle,
      articles: contentArticles
    };
  }
  
  cleanText(text) {
    if (!text) return '';
    return text
      .replace(/\\n/g, ' ')
      .replace(/\\r/g, '')
      .replace(/\\t/g, ' ')
      .replace(/\\"/g, '"')
      .replace(/\\'/g, "'")
      .replace(/\s+/g, ' ')
      .trim();
  }
  
  getExcerpt(article, maxLength = 150) {
    let text = '';
    
    if (article.excerpt) {
      text = this.cleanText(this.stripHTML(article.excerpt));
    } else if (article.contentHtml) {
      text = this.cleanText(this.stripHTML(article.contentHtml));
    }
    
    if (text.length > maxLength) {
      text = text.substring(0, maxLength).trim();
      const lastSpace = text.lastIndexOf(' ');
      if (lastSpace > maxLength - 30) {
        text = text.substring(0, lastSpace);
      }
    }
    
    return text;
  }
  
  renderIssue(issueData) {
    if (this.masthead) {
      const numEl = this.masthead.querySelector('.copper-masthead__number');
      const dateEl = this.masthead.querySelector('.copper-masthead__date');
      const pdfBtn = this.masthead.querySelector('.copper-masthead__pdf');
      
      if (numEl) numEl.innerHTML = `<em>Issue ${issueData.number}</em>`;
      if (dateEl) dateEl.textContent = issueData.date.toUpperCase();
      if (pdfBtn) pdfBtn.dataset.issue = issueData.number;
    }
    
    if (this.editorial) {
      const welcomeEl = this.editorial.querySelector('.copper-editorial__welcome');
      const featuresEl = this.editorial.querySelector('.copper-editorial__features');
      const heroEl = this.editorial.querySelector('.copper-editorial__hero');
      
      if (welcomeEl && issueData.toc) {
        welcomeEl.innerHTML = issueData.toc.contentHtml || '';
      }
      
      if (featuresEl && issueData.articles.length > 0) {
        const featuredHTML = issueData.articles.slice(0, 5).map(article => {
          const title = this.escapeHTML(article.title);
          const author = article.author ? this.escapeHTML(article.author) : '';
          return `
            <a href="/blogs/${this.blogHandle}/${article.handle}" class="copper-editorial__feature-link">
              <span class="copper-editorial__feature-title">${title}</span>
              ${author ? `<span class="copper-editorial__feature-author">${author}</span>` : ''}
            </a>
          `;
        }).join('');
        
        featuresEl.innerHTML = `
          <h3 class="copper-editorial__features-title">Featured</h3>
          ${featuredHTML}
        `;
      }
      
      if (heroEl && issueData.articles.length > 0) {
        const heroArticle = issueData.articles[0];
        const excerpt = this.getExcerpt(heroArticle, 400);
        const title = this.escapeHTML(heroArticle.title);
        const imageUrl = heroArticle.image || '';
        
        heroEl.innerHTML = `
          <a href="/blogs/${this.blogHandle}/${heroArticle.handle}" class="copper-hero-card">
            ${imageUrl ? `
              <div class="copper-hero-card__image">
                <img src="${imageUrl}" alt="${title}" loading="eager">
              </div>
            ` : ''}
            <div class="copper-hero-card__content">
              ${heroArticle.category ? `<span class="copper-hero-card__category">${this.escapeHTML(heroArticle.category)}</span>` : ''}
              <h3 class="copper-hero-card__title">${title}</h3>
              <p class="copper-hero-card__excerpt">${excerpt}${excerpt ? '...' : ''}</p>
              <div class="copper-hero-card__meta">
                ${heroArticle.author ? `<span class="copper-hero-card__author">By ${this.escapeHTML(heroArticle.author)}</span>` : ''}
                <span class="copper-hero-card__cta">Read Article →</span>
              </div>
            </div>
          </a>
        `;
      }
    }
    
    if (this.articleGrid) {
      const gridArticles = this.articleGrid.querySelector('.copper-grid__articles');
      const gridMore = this.articleGrid.querySelector('.copper-grid__more');
      
      if (gridArticles && issueData.articles.length > 1) {
        const accentColors = ['copper', 'forest', 'navy', 'wine', 'slate'];
        
        const gridHTML = issueData.articles.slice(1, 13).map((article, index) => {
          const colorClass = accentColors[index % 5];
          const excerpt = this.getExcerpt(article, 150);
          const title = this.escapeHTML(article.title);
          const imageUrl = article.image || '';
          
          return `
            <article class="copper-story copper-story--${colorClass}">
              <a href="/blogs/${this.blogHandle}/${article.handle}" class="copper-story__link">
                ${imageUrl ? `
                  <div class="copper-story__image">
                    <img src="${imageUrl}" alt="${title}" loading="lazy">
                  </div>
                ` : ''}
                <div class="copper-story__content">
                  ${article.category ? `<span class="copper-story__category">${this.escapeHTML(article.category)}</span>` : ''}
                  <h3 class="copper-story__title">${title}</h3>
                  <p class="copper-story__excerpt">${excerpt}${excerpt ? '...' : ''}</p>
                  <div class="copper-story__footer">
                    ${article.author ? `<span class="copper-story__author">${this.escapeHTML(article.author)}</span>` : ''}
                    <span class="copper-story__cta">Read →</span>
                  </div>
                </div>
              </a>
            </article>
          `;
        }).join('');
        
        gridArticles.innerHTML = gridHTML;
      }
      
      if (gridMore) {
        const remainingArticles = issueData.articles.slice(13);
        if (remainingArticles.length > 0) {
          this.remainingArticles = remainingArticles;
          gridMore.innerHTML = `
            <button type="button" class="copper-more-btn" id="load-more-articles-dynamic">
              Show ${remainingArticles.length} More Articles
            </button>
          `;
          
          document.getElementById('load-more-articles-dynamic')?.addEventListener('click', (e) => {
            this.loadMoreDynamicArticles(e.target);
          });
        } else {
          gridMore.innerHTML = '';
        }
      }
    }
    
    document.querySelectorAll('.copper-issue-card').forEach(card => {
      card.classList.remove('copper-issue-card--active');
      if (parseInt(card.dataset.issue) === issueData.number) {
        card.classList.add('copper-issue-card--active');
      }
    });
  }
  
  loadMoreDynamicArticles(button) {
    const remaining = this.remainingArticles || [];
    const gridArticles = this.articleGrid?.querySelector('.copper-grid__articles');
    
    if (!gridArticles || remaining.length === 0) return;
    
    const accentColors = ['copper', 'forest', 'navy', 'wine', 'slate'];
    
    remaining.forEach((article, index) => {
      const colorClass = accentColors[index % 5];
      const excerpt = this.getExcerpt(article, 150);
      const title = this.escapeHTML(article.title);
      const imageUrl = article.image || '';
      
      const articleEl = document.createElement('article');
      articleEl.className = `copper-story copper-story--${colorClass}`;
      articleEl.style.opacity = '0';
      articleEl.style.transform = 'translateY(20px)';
      
      articleEl.innerHTML = `
        <a href="/blogs/${this.blogHandle}/${article.handle}" class="copper-story__link">
          ${imageUrl ? `
            <div class="copper-story__image">
              <img src="${imageUrl}" alt="${title}" loading="lazy">
            </div>
          ` : ''}
          <div class="copper-story__content">
            ${article.category ? `<span class="copper-story__category">${this.escapeHTML(article.category)}</span>` : ''}
            <h3 class="copper-story__title">${title}</h3>
            <p class="copper-story__excerpt">${excerpt}${excerpt ? '...' : ''}</p>
            <div class="copper-story__footer">
              ${article.author ? `<span class="copper-story__author">${this.escapeHTML(article.author)}</span>` : ''}
              <span class="copper-story__cta">Read →</span>
            </div>
          </div>
        </a>
      `;
      
      gridArticles.appendChild(articleEl);
      
      setTimeout(() => {
        articleEl.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
        articleEl.style.opacity = '1';
        articleEl.style.transform = 'translateY(0)';
      }, index * 50);
    });
    
    button.textContent = 'All Articles Loaded';
    button.disabled = true;
    this.remainingArticles = [];
  }
  
  stripHTML(html) {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  escapeHTML(str) {
    if (!str) return '';
    const stripped = this.stripHTML(str);
    const div = document.createElement('div');
    div.textContent = stripped;
    return div.innerHTML;
  }

  loadMoreArticles() {
    if (!this.hiddenArticles || !this.articlesGrid) return;
    
    const hiddenCards = Array.from(this.hiddenArticles.querySelectorAll('.copper-story'));
    
    hiddenCards.forEach((card, index) => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(20px)';
      this.articlesGrid.appendChild(card);
      
      setTimeout(() => {
        card.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
        card.style.opacity = '1';
        card.style.transform = 'translateY(0)';
      }, index * 50);
    });
    
    this.loadMoreArticlesBtn.textContent = 'All Articles Loaded';
    this.loadMoreArticlesBtn.disabled = true;
  }
  
  async loadMoreIssuesViaAPI() {
    if (this.isLoadingIssues) return;
    
    if (this.lowestIssueLoaded <= 1) {
      this.loadMoreIssuesBtn.textContent = 'All Issues Loaded';
      this.loadMoreIssuesBtn.disabled = true;
      return;
    }
    
    this.isLoadingIssues = true;
    this.loadMoreIssuesBtn.textContent = 'Loading...';
    this.loadMoreIssuesBtn.disabled = true;
    
    try {
      const targetIssues = [];
      for (let i = this.lowestIssueLoaded - 1; i >= Math.max(1, this.lowestIssueLoaded - 12); i--) {
        if (!this.loadedIssues.has(i)) {
          targetIssues.push(i);
        }
      }
      
      if (targetIssues.length === 0) {
        this.loadMoreIssuesBtn.textContent = 'All Issues Loaded';
        this.isLoadingIssues = false;
        return;
      }
      
      const issues = await this.fetchIssuesFromAPI(targetIssues);
      this.renderIssueCards(issues);
      
      const newLowest = Math.min(...targetIssues);
      this.lowestIssueLoaded = newLowest;
      
      if (newLowest <= 1) {
        this.loadMoreIssuesBtn.textContent = 'All Issues Loaded';
        this.loadMoreIssuesBtn.disabled = true;
      } else {
        this.loadMoreIssuesBtn.textContent = 'Load More Issues';
        this.loadMoreIssuesBtn.disabled = false;
      }
      
      if (this.archiveNote) {
        this.archiveNote.textContent = `Showing issues ${this.lowestIssueLoaded}-224. ${this.lowestIssueLoaded > 1 ? 'Click "Load More" to see earlier issues.' : 'All issues loaded!'}`;
      }
      
    } catch (error) {
      console.error('Error loading issues:', error);
      this.loadMoreIssuesBtn.textContent = 'Error - Try Again';
      this.loadMoreIssuesBtn.disabled = false;
    }
    
    this.isLoadingIssues = false;
  }
  
  async fetchIssuesFromAPI(issueNumbers) {
    const tagQueries = issueNumbers.map(num => `tag:"Category_Issue ${num}"`).join(' OR ');
    
    const query = `
      query GetIssueArticles($query: String!, $first: Int!) {
        articles(first: $first, query: $query) {
          edges {
            node {
              id
              title
              handle
              publishedAt
              tags
              image {
                url
              }
              blog {
                handle
              }
            }
          }
        }
      }
    `;
    
    const response = await fetch(`https://${this.shopDomain}/api/2024-01/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.storefrontToken
      },
      body: JSON.stringify({
        query,
        variables: {
          query: tagQueries,
          first: 250
        }
      })
    });
    
    const data = await response.json();
    
    if (data.errors) {
      throw new Error('Failed to fetch issues');
    }
    
    const issuesMap = new Map();
    
    data.data.articles.edges.forEach(({ node }) => {
      let issueNum = null;
      node.tags.forEach(tag => {
        if (tag.startsWith('Category_Issue ')) {
          issueNum = parseInt(tag.replace('Category_Issue ', ''));
        }
      });
      
      if (!issueNum || this.loadedIssues.has(issueNum)) return;
      
      const isTOC = node.title.includes('Table of Contents');
      
      if (!issuesMap.has(issueNum)) {
        issuesMap.set(issueNum, {
          number: issueNum,
          tocArticle: null,
          coverImage: null,
          date: null
        });
      }
      
      const issue = issuesMap.get(issueNum);
      
      if (isTOC) {
        issue.tocArticle = node;
        issue.date = node.publishedAt;
        if (node.image?.url && !issue.coverImage) {
          issue.coverImage = node.image.url;
        }
      } else if (node.image?.url && !issue.coverImage) {
        issue.coverImage = node.image.url;
      }
    });
    
    return Array.from(issuesMap.values())
      .filter(issue => issue.tocArticle)
      .sort((a, b) => b.number - a.number);
  }
  
  renderIssueCards(issues) {
    issues.forEach((issue, index) => {
      if (this.loadedIssues.has(issue.number)) return;
      
      const card = document.createElement('a');
      card.href = `/blogs/${this.blogHandle}?issue=${issue.number}`;
      card.className = 'copper-issue-card';
      card.dataset.issue = issue.number;
      
      const date = new Date(issue.date);
      const monthYear = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      
      if (issue.coverImage) {
        card.innerHTML = `
          <img src="${issue.coverImage}" alt="Issue ${issue.number}" loading="lazy">
          <div class="copper-issue-card__content">
            <span class="copper-issue-card__number">Issue ${issue.number}</span>
            <span class="copper-issue-card__date">${monthYear}</span>
          </div>
        `;
      } else {
        card.innerHTML = `
          <div class="copper-issue-card__placeholder">
            <span>${issue.number}</span>
          </div>
          <div class="copper-issue-card__content">
            <span class="copper-issue-card__number">Issue ${issue.number}</span>
            <span class="copper-issue-card__date">${monthYear}</span>
          </div>
        `;
      }
      
      card.style.opacity = '0';
      card.style.transform = 'translateY(20px)';
      this.archiveGrid.appendChild(card);
      
      setTimeout(() => {
        card.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
        card.style.opacity = '1';
        card.style.transform = 'translateY(0)';
      }, index * 50);
      
      this.loadedIssues.add(issue.number);
    });
  }

  openSearch() {
    if (!this.searchOverlay) return;
    this.searchOverlay.classList.add('active');
    document.body.style.overflow = 'hidden';
    this.allResultItems.forEach(item => item.style.display = '');
    setTimeout(() => this.searchInput?.focus(), 100);
  }

  closeSearch() {
    if (!this.searchOverlay) return;
    this.searchOverlay.classList.remove('active');
    document.body.style.overflow = '';
    if (this.searchInput) this.searchInput.value = '';
    this.allResultItems.forEach(item => item.style.display = '');
  }

  filterArticles(query) {
    const q = query.toLowerCase().trim();
    
    if (!q) {
      this.allResultItems.forEach(item => item.style.display = '');
      return;
    }

    this.allResultItems.forEach(item => {
      const title = item.dataset.title || '';
      const author = item.dataset.author || '';
      const issue = item.dataset.issue || '';
      const matches = title.includes(q) || author.includes(q) || issue.includes(q);
      item.style.display = matches ? '' : 'none';
    });
  }

  async downloadPDF(issueNum) {
    alert('PDF downloads coming soon! This feature is being set up.');
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CopperMagazine());
} else {
  new CopperMagazine();
}

// Shopify theme editor support
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('.copper-magazine')) {
      new CopperMagazine();
    }
  });
}